import React, { useState } from "react";
import {
  CreditCardOffOutlined,
  CreditCardOutlined,
  EventBusyOutlined,
  ListAltOutlined,
  MessageOutlined,
  NewReleasesOutlined,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { Notification } from "../../../types/Notification";
import ApiClient from "../../../services/ApiClient";
import NavigationBarNotificationItemStyle from "./NavigationBarNotificationItemStyle";
import truncate from "truncate-html";
import Grid from "@mui/material/Grid";
import { stripHtml } from "string-strip-html";
import dayjs from "dayjs";

const getIconForType = (type: string) => {
  switch (type) {
    case "release-update":
      return <NewReleasesOutlined />;
    case "new-message":
      return <MessageOutlined />;
    case "check-transfer":
      return <CreditCardOutlined />;
    case "deleted-transfer":
      return <CreditCardOffOutlined />;
    case "paid-task-request":
      return <ListAltOutlined />;
    case "paid-task-overdue":
      return <EventBusyOutlined />;
    default:
      return <NewReleasesOutlined />;
  }
};

export default function NavigationBarNotificationItem({
  notification,
  handleItemClick,
}: {
  notification: Notification;
  handleItemClick?: () => void;
}) {
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  const markAsRead = async (notification: Notification) => {
    if (!notification.isRead) {
      await ApiClient.patch(`notifications/${notification.id}`, {
        body: JSON.stringify({ isRead: true }),
      });
    }
  };

  const getPlainText = (html: string): string => {
    return stripHtml(html).result;
  };

  const handleClick = async () => {
    if (notification.link) {
      window.open(notification.link, "_blank");
    }

    await markAsRead(notification);

    if (handleItemClick) {
      handleItemClick();
    }
  };

  const handleInnerClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (notification.content && getPlainText(notification.content).length > 125) {
      if (!isContentExpanded && !notification.isRead) {
        markAsRead(notification);
      }
      setIsContentExpanded(!isContentExpanded);
    } else {
      handleClick();
    }
  };

  let displayedContent = notification.content || "";
  if (displayedContent && getPlainText(displayedContent).length > 125 && !isContentExpanded) {
    displayedContent = truncate(displayedContent, 125);
  }

  return (
    <Grid
      container
      spacing={0.5}
      onClick={handleClick}
      sx={{
        alignItems: "start",
        justifyContent: "space-between"
      }}>
      <Grid sx={{ paddingRight: "0.5rem" }}>
        {getIconForType(notification.type)}
      </Grid>
      <Grid
        sx={{
          flex: 1
        }}>
        <Typography variant="h6" sx={{ textWrap: "wrap", lineHeight: 1.2 }}>
          <strong>{notification.title}</strong>
        </Typography>
        <Typography variant={"subtitle2"} color="textSecondary" sx={{ mt: "0.25rem" }}>
          {dayjs(notification.created).format("DD.MM.YYYY HH:mm")} Uhr
        </Typography>
        {displayedContent !== "" && (
          <Box
            onClick={handleInnerClick}
            sx={NavigationBarNotificationItemStyle.contentBox}
            dangerouslySetInnerHTML={{ __html: displayedContent }}
          />
        )}
      </Grid>
      <Grid>
        <Box
          sx={{
            ...NavigationBarNotificationItemStyle.notificationBubble,
            visibility: notification.isRead ? "hidden" : "visible",
          }}
        >
          &#9679;
        </Box>
      </Grid>
    </Grid>
  );
}
